import {ButtonCancel, Nav, SaveButton} from '@components/faqs/StyledFAQ';
import {
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import {RootState} from '@/config/store';
import {fetchExpenseAccountInvoices} from '@components/expenseAccountValidation/redux/actions/ExpenseAccountValidationActions';

const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

/** Tope que pide el criterio 8. El mismo que valida el backend. */
const MAX_COMMENTS = 200;
/** El motivo viaja a `expense_account_messages.message`, que no tiene tope de longitud. */
const MAX_REASON = 300;

/**
 * Motivos de rechazo precargados (criterio 8).
 *
 * **PENDIENTE: la lista la define el PO y todavía no la ha entregado.** El PO confirmó que
 * no se administra desde pantalla, así que vive aquí. Cada motivo lleva un `code`, que es
 * lo que conviene guardar de forma estructurada, y su texto por idioma.
 *
 * Mientras esté vacía, la acción de rechazar **no se muestra** en el listado: un modal con
 * un desplegable sin opciones es un callejón sin salida para el revisor.
 */
interface IProps {
    isModalOpen: boolean;
    closeModal: () => void;
    expenseAccount: {folio: string; customAgent: string; customRequest: string} | null;
    /** Devuelve el error a mostrar, o nada si salió bien. */
    onConfirm: (payload: {
        reason: string;
        comments: string;
        invoiceUuid: string;
    }) => Promise<string | void>;
}

/**
 * Modal de rechazo de una Cuenta de Gastos (HU 2, criterios 8 y 9).
 *
 * El selector de factura es opcional: por omisión el motivo y el comentario quedan como
 * nota de toda la Cuenta de Gastos, y solo si el revisor señala una factura se ligan a
 * ella. Así lo confirmó el PO.
 */
const ModalRejectExpenseAccount: React.FC<IProps> = ({
    isModalOpen,
    closeModal,
    expenseAccount,
    onConfirm,
}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const token = useSelector((state: RootState) => state.logins.accessToken);

    const [reason, setReason] = useState('');
    const [comments, setComments] = useState('');
    const [invoiceUuid, setInvoiceUuid] = useState('');
    const [invoices, setInvoices] = useState<
        {uuid: string; invoiceNumber: string; supplierName: string}[]
    >([]);
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);

    useEffect(() => {
        if (!isModalOpen || !expenseAccount) return;
        setReason('');
        setComments('');
        setInvoiceUuid('');
        setFailed(null);
        setSaving(false);
        // Si no se pueden cargar las facturas, el rechazo sigue siendo posible a nivel de
        // Cuenta de Gastos: el selector es opcional.
        fetchExpenseAccountInvoices(token, expenseAccount.customAgent, expenseAccount.customRequest)
            .then(setInvoices)
            .catch(() => setInvoices([]));
    }, [isModalOpen, expenseAccount, token]);

    const confirm = async () => {
        setSaving(true);
        setFailed(null);
        const error = await onConfirm({reason: reason.trim(), comments, invoiceUuid});
        setSaving(false);
        if (error) {
            setFailed(error);
            return;
        }
        closeModal();
    };

    return (
        <StyledModal open={isModalOpen} onClose={saving ? undefined : closeModal}>
            <Box
                sx={{
                    background: '#FFF',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '10px',
                    width: '560px',
                    maxWidth: '92vw',
                }}
            >
                <Nav>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 700,
                            color: 'white',
                            marginLeft: '10px',
                        }}
                    >
                        {t('expense_account_validation.reject.title')}
                    </Typography>
                </Nav>

                <Box sx={{padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px'}}>
                    <Typography sx={{fontSize: '13px', fontWeight: 700}}>
                        {expenseAccount?.folio || expenseAccount?.customRequest}
                    </Typography>

                    {/* El PO descartó el catálogo cerrado: el motivo se escribe a mano y
                        el Agente Aduanal lo consulta en "Ver mensajes". Sigue siendo
                        obligatorio para poder rechazar. */}
                    <TextField
                        required
                        fullWidth
                        multiline
                        rows={2}
                        size="small"
                        disabled={saving}
                        label={t('expense_account_validation.reject.reason')}
                        placeholder={t('expense_account_validation.reject.reason_placeholder')}
                        value={reason}
                        inputProps={{maxLength: MAX_REASON}}
                        InputLabelProps={{shrink: true}}
                        onChange={(event) => setReason(event.target.value)}
                    />

                    <FormControl size="small" fullWidth>
                        <InputLabel shrink>
                            {t('expense_account_validation.reject.invoice')}
                        </InputLabel>
                        <Select
                            notched
                            displayEmpty
                            disabled={saving}
                            value={invoiceUuid}
                            label={t('expense_account_validation.reject.invoice')}
                            onChange={(event) => setInvoiceUuid(event.target.value)}
                        >
                            {/* Criterio 9: por omisión la nota es de toda la Cuenta de
                                Gastos, y señalar una factura es la excepción. */}
                            <MenuItem value="">
                                {t('expense_account_validation.reject.whole_account')}
                            </MenuItem>
                            {invoices.map((invoice) => (
                                <MenuItem key={invoice.uuid} value={invoice.uuid}>
                                    {invoice.invoiceNumber} · {invoice.supplierName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            disabled={saving}
                            label={t('expense_account_validation.reject.comments')}
                            value={comments}
                            inputProps={{maxLength: MAX_COMMENTS}}
                            onChange={(event) => setComments(event.target.value)}
                        />
                        <Typography
                            sx={{
                                fontSize: '11px',
                                textAlign: 'right',
                                marginTop: '4px',
                                color: comments.length >= MAX_COMMENTS ? '#FF5722' : '#6C6C6C',
                            }}
                        >
                            {comments.length}/{MAX_COMMENTS}
                        </Typography>
                    </Box>

                    {failed && (
                        <Typography sx={{fontSize: '12px', color: '#FF5722'}}>{failed}</Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        height: '56px',
                        alignItems: 'center',
                        margin: '0 20px 12px',
                        justifyContent: 'flex-end',
                    }}
                >
                    <ButtonCancel disabled={saving} onClick={closeModal}>
                        {t('expense_account_validation.reject.cancel').toUpperCase()}
                    </ButtonCancel>
                    <SaveButton
                        sx={{padding: '10px'}}
                        // El motivo es obligatorio (criterio 8).
                        disabled={saving || !reason.trim()}
                        onClick={confirm}
                    >
                        {saving ? (
                            <CircularProgress size={16} />
                        ) : (
                            t('expense_account_validation.reject.confirm').toUpperCase()
                        )}
                    </SaveButton>
                </Box>
            </Box>
        </StyledModal>
    );
};

export default ModalRejectExpenseAccount;
